import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  DownloadOutlined,
  ShieldOutlined,
  SearchOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { formatKES } from "../WalletManagement/WalletBalance";
import { companyManagementUrl } from "../../config";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { walletQuery, WALLET_LEDGER_QUERY } from "../../data/WalletService/walletGraphQL";

const TYPE_COLORS = {
  FEE_CHARGE: "warning",
  DISBURSEMENT_SENT: "error",
  TOPUP_MPESA: "success",
  ESCROW_RELEASE: "info",
  WITHDRAWAL_BANK: "default",
};

const WalletAudit = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("ALL");
  const [companies, setCompanies] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const fetchAuditLedger = useCallback(async (companyFilter) => {
    setLoading(true);
    try {
      const gqlData = await walletQuery(WALLET_LEDGER_QUERY, {
        companyId:
          companyFilter && companyFilter !== "ALL" ? parseInt(companyFilter, 10) : null,
        page: 1,
        pageSize: 200,
      });
      if (Array.isArray(gqlData?.walletLedger)) {
        setLedger(
          gqlData.walletLedger.map((e) => {
            const amount = Number(e.amount || 0);
            const after = Number(e.balanceAfter || 0);
            const credit = /TOPUP|REVERSAL|RELEASE/.test(e.txType || "");
            return {
              id: e.id,
              companyId: e.companyId,
              transactionType: e.txType,
              transactionDate: e.createdDate,
              amount,
              balanceAfter: after,
              balanceBefore: credit ? after - amount : after + amount,
              referenceId: e.refId,
              description: e.description,
            };
          })
        );
      } else {
        setLedger([]);
      }
    } catch (err) {
      console.warn("Failed to fetch audit ledger:", err?.message || err);
      setLedger([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const compRes = await fetchJsonDynamic("companies", null, null, companyManagementUrl.uri);
      const raw = compRes?.results;
      const list = Array.isArray(raw)
        ? raw
        : typeof raw === "string"
        ? JSON.parse(raw)
        : [];
      if (list.length) {
        const activeCompanies = list
          .filter((c) => c?.IsActive || c?.isActive)
          .sort((a, b) => (b?.Id || b?.id || 0) - (a?.Id || a?.id || 0));

        setCompanies(
          activeCompanies.map((c) => ({
            id: c.Id ?? c.id,
            name: c.CompanyName ?? c.companyName ?? c.Name ?? c.name ?? `Tenant #${c.Id ?? c.id}`,
          }))
        );
      }
    } catch {
      // Non-critical — filter will still show "ALL"
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchAuditLedger(selectedCompany);
  }, [selectedCompany, fetchAuditLedger]);

  const handleCompanyFilter = (e) => {
    const val = e.target.value;
    setSelectedCompany(val);
  };

  const auditColumns = useMemo(
    () => [
      {
        field: "transactionDate",
        headerName: "Timestamp",
        width: 190,
        renderCell: (params) => (
          <Typography variant="caption">
            {new Date(params.value || params.row.date).toLocaleString()}
          </Typography>
        ),
      },
      {
        field: "companyId",
        headerName: "Company",
        width: 220,
        renderCell: (params) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.row.companyName || companies.find((c) => String(c.id) === String(params.value))?.name || `Tenant #${params.value}`}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              Co #{params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "transactionType",
        headerName: "Type",
        width: 190,
        renderCell: (params) => (
          <Chip
            label={params.value || params.row.type}
            size="small"
            color={TYPE_COLORS[params.value || params.row.type] || "default"}
            variant="outlined"
          />
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatKES(params.value)}
          </Typography>
        ),
      },
      {
        field: "balanceDelta",
        headerName: "Balance Delta",
        width: 240,
        valueGetter: (params) => `${params.row.balanceBefore} -> ${params.row.balanceAfter}`,
        renderCell: (params) => (
          <Typography variant="caption">
            {formatKES(params.row.balanceBefore)} → {formatKES(params.row.balanceAfter)}
          </Typography>
        ),
      },
      {
        field: "receipt",
        headerName: "Receipt",
        width: 180,
        renderCell: (params) => (
          <code style={{ fontSize: "0.75rem", color: colors.grey[300] }}>
            {params.row.mpesaReceiptNumber || params.row.receipt || params.row.referenceId || "—"}
          </code>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        width: 260,
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <Typography variant="body2" noWrap sx={{ fontSize: "0.82rem", color: colors.grey[300] }}>
              {params.value || "—"}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [companies, colors]
  );

  return (
    <Card
      sx={{
        borderRadius: 2.5,
        backgroundColor: isDark ? colors.primary[400] : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 3, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ShieldOutlined sx={{ color: "#22bf72" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                Cross-Company Financial Audit Ledger
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              System-wide audit trail ensuring ledger-balance reconciliation and double-debit prevention.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh audit log">
              <IconButton
                size="small"
                onClick={() => fetchAuditLedger(selectedCompany)}
                disabled={loading}
              >
                {loading
                  ? <CircularProgress size={16} />
                  : <RefreshOutlined sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadOutlined />}
              sx={{ textTransform: "none" }}
            >
              Export Audit Log
            </Button>
          </Stack>
        </Box>

        {/* Company filter */}
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Filter by Company"
            value={selectedCompany}
            onChange={handleCompanyFilter}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: <SearchOutlined sx={{ mr: 1, fontSize: 18, color: colors.grey[400] }} />,
            }}
          >
            <MenuItem value="ALL">All Companies</MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : ledger.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
              No audit entries found for the selected filter. Transactions will appear here once wallet activity occurs.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              "& .MuiDataGrid-root": {
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                borderRadius: 2,
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: isDark ? colors.primary[500] : "#f1f5f9",
                fontWeight: 700,
                borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1"}`,
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9"}`,
              },
              "& .MuiDataGrid-virtualScroller": {
                "&::-webkit-scrollbar": {
                  height: "12px",
                  width: "12px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.25)" : "#94a3b8",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.35)" : "#64748b",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              },
            }}
          >
            <DataGrid
              autoHeight
              rows={ledger}
              columns={auditColumns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableSelectionOnClick
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WalletAudit;
